import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ConfiguratorEtlService, EtlConf} from '@tj/tj-dmp-lib-api';
import {SelectItem, SelectItemGroup} from 'primeng/api';
import {MultiSelectModule} from 'primeng/multiselect';
import {TranslateModule} from '@ngx-translate/core';

@Component({
    selector: 'dmp-select-etls',
    templateUrl: './select-etls.component.html',
    styleUrls: ['./select-etls.component.scss'],
    imports: [
        MultiSelectModule,
        FormsModule,
        TranslateModule
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: SelectEtlsComponent
        }
    ]
})
export class SelectEtlsComponent implements OnChanges, ControlValueAccessor {
    @Input() value: string[] = [];
    /** Valeur affichée dans le multiSelect : si `value` est vide, on affiche tout comme sélectionné (cosmétique, la vraie valeur du filtre reste vide = pas de filtre) */
    public displayValue: string[] = [];
    @Input() tenant!: string;
    @Input() type!: string[];
    @Input() appendTo: any = 'body';
    @Input() disabled = false;

    /** Émission externe des changements */
    @Output() change = new EventEmitter<string[]>();

    /** Liste brute d’ETLs récupérée depuis l’API */
    public etlList: EtlConf[] = [];
    /** Options pour PrimeNG MultiSelect */
    public etlOptions: SelectItem[] = [];
    public etlGroupOptions: SelectItemGroup[] = [];

    // Callbacks fournis par Angular forms
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private onChangeFn: (value: string[]) => void = () => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private onTouchedFn: () => void = () => {};

    constructor(private readonly configuratorEtlService: ConfiguratorEtlService) {

    }

    ngOnChanges(changes: SimpleChanges): void {
        // À chaque nouveau tenant/type, rafraîchir la liste
        if (changes.tenant || changes.type) {
            this.loadEtls();
        }
    }

    private loadEtls(): void {
        if (this.tenant){
            const type = this.type?.map(t => t.replace('_DATA', ''));
            this.configuratorEtlService.getAllEtls(this.tenant, type).subscribe((res: EtlConf[]) => {
                this.etlList = res;
                this.etlOptions = res.map(e => ({ label: e.name, value: e.id }));
                // Construire la map de grouping
                const map = new Map<string, SelectItem[]>();
                res.forEach(e => {
                    const group = e.typeJob ?? 'IMPORT';
                    if (!map.has(group)) {
                        map.set(group, []);
                    }
                    // tslint:disable-next-line:no-non-null-assertion
                    map.get(group)?.push({ label: e.name, value: e.id });
                });
                // Transformer en tableau groupé
                this.etlGroupOptions = Array.from(map.entries()).map(([label, items]) => ({ label, items }));
                this.updateDisplayValue();
                // Notifier le form control si la valeur a déjà été initialisée
                this.propagateChanges();
            });
        }
    }

    /** Si aucune sélection explicite, affiche tout comme coché (la vraie valeur du filtre reste vide = pas de filtre) */
    private updateDisplayValue(): void {
        this.displayValue = this.value.length ? this.value : this.etlOptions.map(o => o.value);
    }

    // ################################
    // Implementation de ControlValueAccessor
    // ################################

    writeValue(value: string[]): void {
        // Appelé par Angular pour mettre à jour la vue quand formControl.setValue(...)
        this.value = value || [];
        this.updateDisplayValue();
    }

    registerOnChange(fn: (value: string[]) => void): void {
        this.onChangeFn = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    // ################################
    // Helpers
    // ################################

    /** Émettre en sortie + notifier Angular forms */
    private propagateChanges(): void {
        this.onChangeFn(this.value);
        this.onTouchedFn();
        this.change.emit(this.value);
    }

    /** Au clic / sélection dans le multiSelect */
    public onMultiSelectChange(selected: string[]): void {
        this.displayValue = selected;
        // Si l'utilisateur sélectionne tout explicitement, on repasse en "pas de filtre"
        // (équivalent fonctionnellement, et reste correct si de nouveaux flux apparaissent)
        this.value = (selected.length === this.etlOptions.length) ? [] : selected;
        this.propagateChanges();
    }

    /** Une sélection vide équivaut à "pas de filtre" : on revient visuellement à "tout" à la fermeture du panneau */
    public onPanelHide(): void {
        if (!this.displayValue.length) {
            this.value = [];
            this.updateDisplayValue();
            this.propagateChanges();
        }
    }
}
